import { Link } from 'react-router-dom';
import { useFetch } from '../../../hooks/useFetch';
import { api } from '../../../services/api/apiClient';
import { Card, EmptyState, ErrorState, Skeleton } from '../../ui/Display/Display';
import { StatusBadge } from '../Parts/Parts';
import type { ReactNode } from 'react';
import type { StatusMap } from '../../../types/common';

/**
 * Short list of documents linked to a record (e.g. a customer's invoices).
 * row(r) -> { to, title, sub, amount, status }.
 */
export interface RelatedRow {
  to: string;
  title: ReactNode;
  sub?: ReactNode;
  amount?: ReactNode;
  status?: string;
  extra?: ReactNode;
}

export function RelatedList({ path, query, row, statusMap, emptyTitle, emptyMessage, icon = 'receipt', action }: {
  path: string;
  query?: Record<string, any>;
  row: (r: any) => RelatedRow;
  statusMap?: StatusMap;
  emptyTitle?: ReactNode;
  emptyMessage?: ReactNode;
  icon?: string;
  action?: ReactNode;
}) {
  const { data, loading, error, reload } = useFetch<any[]>(() => api.get(path, { limit: 50, ...query }).then((r) => r.data), [path, JSON.stringify(query)]);

  return (
    <Card className="overflow-hidden">
      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : loading && !data ? (
        <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10" />)}</div>
      ) : !data || data.length === 0 ? (
        <EmptyState icon={icon} title={emptyTitle} message={emptyMessage} action={action} />
      ) : (
        <ul className="divide-y divide-line">
          {data.map((r) => {
            const m = row(r);
            return (
              <li key={r._id}>
                <Link to={m.to} className="flex items-center gap-4 px-5 py-3 outline-none hover:bg-navy-50/60 focus-visible:bg-navy-50">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-navy-900">{m.title}</p>
                    {m.sub && <p className="truncate text-xs text-ink-500">{m.sub}</p>}
                  </div>
                  {m.status && statusMap && <StatusBadge map={statusMap} value={m.status} />}
                  {m.extra}
                  {m.amount != null && <span className="tabular w-28 text-right text-sm font-semibold text-navy-900">{m.amount}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export default RelatedList;
